import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const SmsTerms: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAuthenticated = !!user;

  return (
    <MobileLayout showNav={isAuthenticated} showHeader={isAuthenticated}>
      <div className="flex-1 flex flex-col">
        {/* Header - Only show when not authenticated */}
        {!isAuthenticated && (
          <div className="px-6 py-4 border-b border-dark-border dark:border-dark-border border-light-border">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
              >
                <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
              </button>
              <h1 className="ml-4 text-xl font-semibold text-dark-text dark:text-dark-text text-light-text">
                {t('legal.smsTerms.title', 'SMS Terms of Service')}
              </h1>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="prose prose-invert max-w-none">
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t('legal.smsTerms.lastUpdated', 'Last updated')}:{' '}
              {new Date().toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
            </p>

            {/* Program Name */}
            <div className="bg-dark-card dark:bg-dark-card bg-light-surface p-4 rounded-xl mb-6">
              <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-2">
                {t('legal.smsTerms.programName', 'Program Name')}
              </h2>
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t('legal.smsTerms.programNameValue', 'Chat Nexus AI Companion Messaging')}
              </p>
            </div>

            {/* Consent */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              1. {t('legal.smsTerms.consent.title', 'Consent')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.consent.content',
                'By providing your phone number and opting in, you agree to receive SMS messages from Chat Nexus including authentication codes, account notifications, and AI companion messages.',
              )}
            </p>

            {/* Message Frequency */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              2. {t('legal.smsTerms.frequency.title', 'Message Frequency')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.frequency.content',
                'Message frequency varies based on your usage and companion interactions. You may receive authentication codes when logging in and messages from your AI companion based on your preferences.',
              )}
            </p>

            {/* Message and Data Rates */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              3. {t('legal.smsTerms.rates.title', 'Message and Data Rates')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.rates.content',
                'Standard message and data rates may apply. Contact your wireless carrier for details about your messaging plan.',
              )}
            </p>

            {/* Opt-Out Instructions */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              4. {t('legal.smsTerms.optOut.title', 'How to Opt Out')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
              {t(
                'legal.smsTerms.optOut.content',
                'You can opt out of SMS messages at any time by:',
              )}
            </p>
            <ul className="list-disc pl-6 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              <li>
                {t(
                  'legal.smsTerms.optOut.method1',
                  'Replying STOP to any SMS message you receive from us',
                )}
              </li>
              <li>
                {t(
                  'legal.smsTerms.optOut.method2',
                  'Managing your SMS preferences in your account settings',
                )}
              </li>
              <li>{t('legal.smsTerms.optOut.method3', 'Contacting support@chatnexus.com')}</li>
            </ul>

            {/* Help */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              5. {t('legal.smsTerms.help.title', 'Help')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.help.content',
                'For help, reply HELP to any SMS message or contact support@chatnexus.com. You can also visit our support page at https://chatnexus.com/support.',
              )}
            </p>

            {/* Privacy */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              6. {t('legal.smsTerms.privacy.title', 'Privacy')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.privacy.content',
                'Your mobile phone number and information will not be sold or shared with third parties for promotional or marketing purposes. For more information, please review our Privacy Policy.',
              )}
            </p>

            {/* Not Required for Purchase */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              7. {t('legal.smsTerms.notRequired.title', 'Not Required for Purchase')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.notRequired.content',
                'Consent to receive SMS messages is not a condition of purchase. You can use Chat Nexus without opting into SMS messaging.',
              )}
            </p>

            {/* Supported Carriers */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              8. {t('legal.smsTerms.carriers.title', 'Supported Carriers')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.smsTerms.carriers.content',
                'SMS messaging is supported on all major US carriers including AT&T, Verizon, T-Mobile, Sprint, and others. Carrier message and data rates may apply.',
              )}
            </p>

            {/* Contact */}
            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              9. {t('legal.smsTerms.contact.title', 'Contact Us')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-6">
              {t(
                'legal.smsTerms.contact.content',
                'For questions about these SMS Terms, please contact us at support@chatnexus.com',
              )}
            </p>

            {/* Quick Reference Box */}
            <div className="bg-primary-500/10 border border-primary-500/30 p-4 rounded-xl">
              <h3 className="font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
                {t('legal.smsTerms.quickReference.title', 'Quick Reference')}
              </h3>
              <ul className="space-y-2 text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                <li>
                  <span className="font-medium text-dark-text dark:text-dark-text text-light-text">
                    STOP:
                  </span>{' '}
                  {t('legal.smsTerms.quickReference.stop', 'Reply to opt out of all SMS messages')}
                </li>
                <li>
                  <span className="font-medium text-dark-text dark:text-dark-text text-light-text">
                    HELP:
                  </span>{' '}
                  {t('legal.smsTerms.quickReference.help', 'Reply to get support information')}
                </li>
                <li>
                  <span className="font-medium text-dark-text dark:text-dark-text text-light-text">
                    {t('legal.smsTerms.quickReference.frequency', 'Frequency')}:
                  </span>{' '}
                  {t('legal.smsTerms.quickReference.frequencyValue', 'Message frequency varies')}
                </li>
                <li>
                  <span className="font-medium text-dark-text dark:text-dark-text text-light-text">
                    {t('legal.smsTerms.quickReference.rates', 'Rates')}:
                  </span>{' '}
                  {t('legal.smsTerms.quickReference.ratesValue', 'Msg & data rates may apply')}
                </li>
              </ul>
            </div>

            {/* Links to other legal pages */}
            <div className="mt-6 pt-6 border-t border-dark-border dark:border-dark-border border-light-border">
              <p className="text-sm text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-2">
                {t('legal.smsTerms.relatedLinks', 'Related Legal Documents')}:
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/terms"
                  className="text-primary-500 hover:text-primary-400 text-sm underline"
                >
                  {t('legal.terms.title', 'Terms of Service')}
                </a>
                <a
                  href="/privacy"
                  className="text-primary-500 hover:text-primary-400 text-sm underline"
                >
                  {t('legal.privacy.title', 'Privacy Policy')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default SmsTerms;
